"use client";

import { FiGift } from "react-icons/fi";
import type { Market } from "./types";
import { formatCurrency } from "@/lib/format";
import { useMarketsRewardsParams } from "@/hooks/useMarketRewardsParams";
import { useT } from "@/hooks/useT";

interface Props {
  markets: Market[] | undefined;
}

// Outlined-gift repeating pattern as a CSS data URI. Rendered at low opacity
// behind the content so the card reads as "rewards-y" without drowning
// the numbers underneath.
const GIFT_PATTERN_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 24 24' fill='none' stroke='#CCFF00' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'>
    <polyline points='20 12 20 22 4 22 4 12'/>
    <rect x='2' y='7' width='20' height='5'/>
    <line x1='12' y1='22' x2='12' y2='7'/>
    <path d='M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z'/>
    <path d='M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z'/>
  </svg>`
)}`;

/**
 * Event-level liquidity-rewards aggregate. Fetches the CLOB rewards params
 * for every child market in one batched request (/api/markets/rewards?
 * conditionIds=…) and sums/min/max locally. Renders nothing if the child
 * markets have no pools (common on non-rewards events).
 */
export default function EventRewardsBadge({ markets }: Props) {
  const t = useT();
  const conditionIds = (markets || [])
    .map((m) => m.conditionId)
    .filter((x): x is string => !!x);
  const { data: rewards } = useMarketsRewardsParams(conditionIds);

  if (!markets || markets.length === 0 || !rewards) return null;

  const rewardedEntries = markets
    .map((m) => ({ market: m, params: rewards[m.conditionId] }))
    .filter((x) => x.params && x.params.ratePerDay > 0);
  if (rewardedEntries.length === 0) return null;

  const totalPool = rewardedEntries.reduce((s, x) => s + x.params.ratePerDay, 0);
  const tightestSpread = rewardedEntries.reduce(
    (min, x) => (x.params.maxSpread > 0 ? Math.min(min, x.params.maxSpread) : min),
    Number.POSITIVE_INFINITY,
  );
  const highestMinSize = rewardedEntries.reduce(
    (max, x) => Math.max(max, x.params.minSize || 0),
    0,
  );

  return (
    <div className="relative rounded-[5px] overflow-hidden bg-white dark:bg-[#18181D]">
      {/* Very subtle gift watermark. Drops to ~3% so the card stops
          screaming "NEON REWARDS" and just reads as a quiet textured
          surface consistent with the rest of the event cards. */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025] dark:opacity-[0.035]"
        style={{
          backgroundImage: `url("${GIFT_PATTERN_SVG}")`,
          backgroundSize: "48px 48px",
          backgroundRepeat: "repeat",
        }}
      />

      <div className="relative px-3 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FiGift className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
              {t("rewardsBadge.title")}
            </div>
            <div className="text-[11px] text-gray-600 dark:text-gray-400">
              {t(
                markets.length === 1 ? "rewardsBadge.summary.one" : "rewardsBadge.summary.many",
                { n: rewardedEntries.length, total: markets.length },
              )}
              {highestMinSize > 0 && (
                <> · {t("rewardsBadge.minShares", { n: highestMinSize })}</>
              )}
              {Number.isFinite(tightestSpread) && (
                <> · {t("rewardsBadge.tightestSpread", { spread: `${tightestSpread.toFixed(1)}¢` })}</>
              )}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{t("rewardsBadge.pool")}</div>
          <div className="text-lg font-bold leading-tight tabular-nums text-gray-900 dark:text-white">
            {formatCurrency(totalPool)}<span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{t("rewardsBadge.perDay")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
